import { Check } from "lucide-react";
import { deriveDeskIdentity } from "@/lib/setup/deskIdentity";
import type { SetupDeskSlot, SetupFlowAnswers, SetupFlowDefinition } from "@/types/setup/flow";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface GenieFrontDeskPreviewProps {
  flow: SetupFlowDefinition;
  answers: SetupFlowAnswers;
  /**
   * How many questions the user has reached this run. Rows only reveal in
   * order as they progress — never from pre-filled draft values ahead of the
   * current step — so the desk always assembles as a truthful story.
   */
  revealedCount: number;
  /** The row currently being filled — gets a highlighted, live-reacting state. */
  activeSlot?: SetupDeskSlot | null;
  /** Finale: the desk is complete and the genie comes to life. */
  awake?: boolean;
}

/**
 * The living "front desk" — the thing the customer is building. It assembles
 * one row at a time as answers land, and wakes up at the finale. This is the
 * artifact that turns the collection from a form into a story.
 */
export const GenieFrontDeskPreview = ({
  flow,
  answers,
  revealedCount,
  activeSlot = null,
  awake = false,
}: GenieFrontDeskPreviewProps) => {
  const identity = deriveDeskIdentity(answers.website);
  const knowledgeIndex = flow.questions.findIndex((q) => q.deskSlot === "knowledge");
  const identityRevealed = knowledgeIndex >= 0 && knowledgeIndex < revealedCount;
  const deskName = identityRevealed && identity ? identity.name : "Your club";

  const filledCount = flow.questions.filter(
    (q, idx) => idx < revealedCount && (answers[q.id] || "").trim(),
  ).length;
  const progress = Math.round((filledCount / flow.questions.length) * 100);

  const displayValue = (slot: SetupDeskSlot, raw: string): string => {
    const value = raw.trim();
    if (!value) return "";
    // The knowledge row reads cleaner as the bare domain than the raw URL.
    if (slot === "knowledge" && identity) return identity.domain;
    return value;
  };

  return (
    <div className="relative w-full max-w-[420px]">
      {/* Soft brand glow so the card reads as a hero, not a plain box */}
      <div className="pointer-events-none absolute -inset-6 rounded-[32px] bg-gradient-to-br from-genie-primary/15 to-genie-secondary/15 blur-2xl" />

      {/* The front-desk card */}
      <div className="relative w-full rounded-[24px] border border-[#E5EAF0] bg-white p-5 text-[#222B45] shadow-2xl shadow-genie-primary/10">
        {/* Header: identity + status */}
        <div className="flex items-center gap-3">
          <div className="relative h-11 w-11 flex-shrink-0 rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] p-[2px]">
            <div className="flex h-full w-full items-center justify-center rounded-[14px] bg-white">
              <GenieIcon name="chat" size={24} className="h-6 w-6" />
            </div>
            {awake && (
              <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] ring-2 ring-white">
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-bold leading-tight text-[#222B45]">
              {deskName}
            </p>
            <p className="text-[11px] font-medium text-[#64748B]">Front desk</p>
          </div>
          <StatusChip awake={awake} progress={progress} />
        </div>

        {/* Greeting bubble — appears once we know the club */}
        <div
          className={`mt-4 rounded-2xl rounded-tl-md bg-genie-background px-4 py-3 text-[13px] leading-snug text-[#222B45] transition-all duration-500 ${
            identityRevealed && identity ? "opacity-100" : "opacity-45"
          }`}
        >
          {identityRevealed && identity ? (
            <>
              Hi! I'm the front desk for <span className="font-semibold">{deskName}</span> — how can
              I help?
            </>
          ) : (
            <span className="text-[#64748B]">Your genie's greeting will appear here…</span>
          )}
        </div>

        {/* Assembling rows */}
        <div className="mt-4 space-y-2">
          {flow.questions.map((question, idx) => {
            const revealed = idx < revealedCount;
            const raw = answers[question.id] || "";
            const value = revealed ? displayValue(question.deskSlot, raw) : "";
            const filled = Boolean(value);
            const active = activeSlot === question.deskSlot;
            const Icon = question.icon;

            return (
              <div
                key={question.id}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-all duration-300 ${
                  filled
                    ? "border-genie-border bg-white"
                    : active
                      ? "border-genie-primary bg-genie-primary/5 ring-2 ring-genie-primary/20"
                      : "border-dashed border-genie-border/70 bg-genie-background/50"
                }`}
              >
                <span
                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
                    filled ? "bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" : "bg-genie-background"
                  }`}
                >
                  <Icon
                    className={`h-4 w-4 ${filled ? "text-white" : "text-gray-400"}`}
                    strokeWidth={1.9}
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[10.5px] font-semibold uppercase tracking-wide text-[#64748B]">
                    {question.deskRowLabel}
                  </p>
                  <p
                    className={`truncate text-[13.5px] font-semibold ${
                      filled ? "text-[#222B45]" : active ? "text-genie-primary" : "text-gray-400"
                    }`}
                  >
                    {filled ? value : active ? question.reaction : "—"}
                  </p>
                </div>
                <Check
                  className={`h-4 w-4 flex-shrink-0 text-genie-primary transition-all duration-300 ${
                    filled ? "scale-100 opacity-100" : "scale-0 opacity-0"
                  }`}
                  strokeWidth={3}
                />
              </div>
            );
          })}
        </div>

        {/* Assembling progress */}
        <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-genie-background">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-500 ease-out"
            style={{ width: `${awake ? 100 : progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const StatusChip = ({ awake, progress }: { awake: boolean; progress: number }) => {
  if (awake) {
    return (
      <span className="flex flex-shrink-0 items-center gap-1.5 rounded-full bg-genie-primary/10 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide text-genie-primary">
        <span className="h-1.5 w-1.5 rounded-full bg-genie-primary shadow-[0_0_6px_#4E9CFF]" />
        Ready
      </span>
    );
  }
  return (
    <span className="flex flex-shrink-0 items-center gap-1.5 rounded-full bg-genie-background px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide text-[#64748B]">
      <span className="h-1.5 w-1.5 animate-gentle-pulse rounded-full bg-genie-secondary" />
      {progress === 0 ? "New" : "Building"}
    </span>
  );
};
